import AppContext from 'App/AppContext';
import { IPPrimaryButton, IPText } from 'baseComponents';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPStack } from 'baseComponents/Stack/IPStack';
import RichTextEditor, { IRichTextEditor } from 'components/Text/RichTextEditor';
import { globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import User from 'models/user';
import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IAddCommentProps {
  onSend: (newComment: string, mentions: User[]) => void;
  isActionPending: boolean;
}

//
// In this component, features to mentions have been disabled
//
const AddComment = (props: IAddCommentProps) => {
  const { t } = useTranslation(['translation', 'commentTrail', 'activity']);
  const appContext = useContext(AppContext);
  const [value, setValue] = useState<string>('');
  const [checkInputHasFocus, setCheckInputHasFocus] = useState<boolean>(false);
  const editorRef = useRef<IRichTextEditor | null>(null);

  const setNewValue = (newValue: string | undefined) => {
    setValue(newValue ? newValue : '');
  };

  const sendMessage = (newValue: string | undefined) => {
    try {
      props.onSend(newValue || '', []);
      setValue('');
      editorRef.current?.focus();
    } catch (error) {
      console.error(error);
    }
  };

  const onKeyDown = (e: Partial<React.KeyboardEvent>) => {
    if (e.ctrlKey === true && e.key === 'Enter' && value.length > 0) {
      sendMessage(value);
    }
  };

  //
  // Main render
  //
  return (
    <IPStack horizontal tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPPersona
          imageUrl={appContext.user.getPhotoURLObj()}
          text={appContext.user.name}
          hidePersonaDetails={true}
          size={IPPersonaSize.size32}
        />
      </IPStack.Item>
      <IPStack.Item grow>
        <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { paddingBottom: 10 } }}>
          <IPStack.Item>
            <RichTextEditor
              ref={editorRef}
              placeholder={t('commentTrail:Add-placeholder')}
              placeholderReadonly={t('commentTrail:Readonly-placeholder')}
              html={value}
              maxLength={4000}
              onChange={(value) => {
                setNewValue(value);
              }}
              disallowFullscreen={true}
              maxHeight={300}
              compactToolbar={true}
              onFocus={() => setCheckInputHasFocus(true)}
              onBlur={() => {
                setCheckInputHasFocus(false);
              }}
              hideRemainingChars={appContext.isMobileView}
              onKeyDown={onKeyDown}
              onAccept={
                !appContext.isMobileView
                  ? undefined
                  : (value, accept) => {
                      if (accept && value) {
                        sendMessage(value);
                      } else {
                        editorRef.current?.blur();
                      }
                    }
              }
            />
          </IPStack.Item>
          {!appContext.isMobileView && (
            <IPStack.Item>
              <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
                <IPPrimaryButton
                  disabled={props.isActionPending || value === ''}
                  onClick={() => {
                    sendMessage(value);
                  }}
                >
                  {t('commentTrail:Send')}
                </IPPrimaryButton>
                {checkInputHasFocus && <IPText variant="small">{t('activity:KeyTipNew')}</IPText>}
              </IPStack>
            </IPStack.Item>
          )}
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );
};

export default AddComment;
